import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Modal, Spinner } from 'react-bootstrap';
import rendezVousService from '../../../services/rendezVousService';
import receptionService from '../../../services/receptionService';
import externalMemberService from '../../../services/externalMemberService';
import Icon from '../../common/Icons';
import { useDoctorWorkspace } from '../../doctor/doctorWorkspaceContext';
import { computeFloatingMenuPosition, estimateActionMenuHeight } from '../../../utils/floatingMenuPosition';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { HeadDoctorPageHeader } from '../ui';
import AppointmentListItem from './AppointmentListItem';
import AppointmentFormModal from './AppointmentFormModal';
import {
  APPOINTMENT_STATUS,
  EMPTY_STATUS_BREAKDOWN,
  computeAppointmentStats,
  computeAppointmentStatusBreakdown,
  formatDoctorName,
  formatPatientLabel,
  formatSlotRange,
  getAppointmentStatusFilterProps,
  getAppointmentStatusPillProps
} from './appointmentListDisplay';
import {
  formatDateTimeForDb,
  toDateTimeLocalValue,
  todayYmd,
  parseToInstant
} from '../../../utils/appTimezone';
import '../HeadDoctorDashboard.css';

const STATUS_FILTERS = [
  { id: '', labelKey: 'FILTER_STATUS_ALL' },
  ...Object.values(APPOINTMENT_STATUS).map((s) => ({
    id: s.id,
    labelKey: `STATUS_${s.id}`
  }))
];

const EMPTY_FORM = {
  doctor_id: '',
  start_at: '',
  end_at: '',
  motif: '',
  notes: '',
  patient_type: 'SMIE',
  patient_id: ''
};

const CONFIRM_LABELS = {
  confirm: 'Confirmer',
  finish: 'Terminer',
  cancel: 'Annuler',
  absent: 'Marquer absent'
};

export default function HeadDoctorAppointmentsManager() {
  const copy = HEAD_DOCTOR_COPY.APPOINTMENTS;
  const { toast } = useDoctorWorkspace();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [items, setItems] = useState([]);
  const [calendarRows, setCalendarRows] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [viewingId, setViewingId] = useState(null);
  const [actionMenuItem, setActionMenuItem] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0, placement: 'below' });
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmReason, setConfirmReason] = useState('');
  const [statusBreakdown, setStatusBreakdown] = useState(EMPTY_STATUS_BREAKDOWN);
  const [statusBreakdownLoading, setStatusBreakdownLoading] = useState(true);

  const filters = useMemo(
    () => ({
      status: statusFilter || undefined,
      doctor_id: doctorFilter || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo ? `${dateTo}T23:59:59` : undefined,
      search: search || undefined
    }),
    [statusFilter, doctorFilter, dateFrom, dateTo, search]
  );

  const filtersWithoutStatus = useMemo(
    () => ({
      doctor_id: doctorFilter || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo ? `${dateTo}T23:59:59` : undefined,
      search: search || undefined
    }),
    [doctorFilter, dateFrom, dateTo, search]
  );

  const loadDoctors = useCallback(async () => {
    const res = await rendezVousService.doctors();
    if (res.success) {
      const list = Array.isArray(res.data) ? res.data : [];
      setDoctors(list);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await rendezVousService.list({ ...filters, limit: 100, offset: 0 });
      if (res.success) {
        setItems(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res.message || 'Chargement impossible');
        setItems([]);
      }
    } catch (e) {
      setError(e.message || 'Chargement impossible');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadStatusBreakdown = useCallback(async () => {
    setStatusBreakdownLoading(true);
    try {
      const res = await rendezVousService.list({ ...filtersWithoutStatus, limit: 500, offset: 0 });
      if (res.success) {
        setStatusBreakdown(computeAppointmentStatusBreakdown(res.data));
      }
    } catch {
      setStatusBreakdown(EMPTY_STATUS_BREAKDOWN);
    } finally {
      setStatusBreakdownLoading(false);
    }
  }, [filtersWithoutStatus]);

  const loadCalendar = useCallback(async () => {
    try {
      const res = await rendezVousService.calendar({
        doctor_id: doctorFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined
      });
      if (res.success) {
        setCalendarRows(Array.isArray(res.data) ? res.data : []);
      }
    } catch {
      setCalendarRows([]);
    }
  }, [doctorFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadDoctors();
  }, [loadDoctors]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadItems();
    loadCalendar();
    loadStatusBreakdown();
  }, [loadItems, loadCalendar, loadStatusBreakdown]);

  useEffect(() => {
    const shouldCheck = form.doctor_id && form.start_at && form.end_at && showFormModal;
    if (!shouldCheck) {
      setAvailability(null);
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      try {
        const res = await rendezVousService.availability({
          doctor_id: form.doctor_id,
          start_at: formatDateTimeForDb(form.start_at),
          end_at: formatDateTimeForDb(form.end_at),
          ...(editingId ? { exclude_id: editingId } : {})
        });
        if (res.success) setAvailability(res.data || null);
      } catch (e) {
        setAvailability({ available: false, message: e.message });
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [form.doctor_id, form.start_at, form.end_at, editingId, showFormModal]);

  useEffect(() => {
    const q = patientSearch.trim();
    if (!showFormModal || q.length < 2) {
      setPatientOptions([]);
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      setPatientSearchLoading(true);
      try {
        if (form.patient_type === 'SMIE') {
          const res = await receptionService.searchMembers(q, { limit: 15 });
          setPatientOptions(
            (res?.data || []).map((m) => ({
              id: m.id,
              label:
                `${m.nom || ''} ${m.prenom || ''}`.trim() +
                (m.type !== 'AGENT' ? ' (ayant droit)' : '')
            }))
          );
        } else {
          const res = await externalMemberService.list({ search: q, limit: 15, offset: 0 });
          setPatientOptions(
            (res?.data || []).map((m) => ({
              id: m.id,
              label: `${m.nom || ''} ${m.prenom || ''}`.trim()
            }))
          );
        }
      } catch {
        setPatientOptions([]);
      } finally {
        setPatientSearchLoading(false);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [patientSearch, form.patient_type, showFormModal]);

  const stats = useMemo(() => computeAppointmentStats(items), [items]);

  const hasActiveFilters =
    Boolean(searchInput.trim()) ||
    Boolean(statusFilter) ||
    Boolean(doctorFilter) ||
    Boolean(dateFrom) ||
    Boolean(dateTo);

  const groupedCalendar = useMemo(() => {
    const groups = new Map();
    calendarRows.forEach((row) => {
      const key = todayYmd(parseToInstant(row.start_at));
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [calendarRows]);

  const resetFilters = () => {
    setSearchInput('');
    setStatusFilter('');
    setDoctorFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const closeActionMenu = () => setActionMenuItem(null);

  const openActionMenu = (event, item) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const actionCount =
      1 +
      (item.status === 'EN_ATTENTE' || item.status === 'CONFIRME' ? 1 : 0) +
      (item.status === 'EN_ATTENTE' ? 1 : 0) +
      (item.status === 'CONFIRME' ? 1 : 0) +
      (item.status === 'EN_ATTENTE' || item.status === 'CONFIRME' ? 2 : 0);
    setActionMenuPos(
      computeFloatingMenuPosition(rect, {
        menuWidth: 220,
        menuHeight: estimateActionMenuHeight(Math.min(actionCount, 6), { withSeparator: true })
      })
    );
    setActionMenuItem(item);
  };

  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
      doctor_id: doctors[0] ? String(doctors[0].id) : ''
    });
    setEditingId(null);
    setPatientSearch('');
    setPatientOptions([]);
    setModalError('');
    setAvailability(null);
  };

  const openCreate = () => {
    closeActionMenu();
    resetForm();
    setShowFormModal(true);
  };

  const openEdit = (item) => {
    closeActionMenu();
    setEditingId(item.id);
    setForm({
      doctor_id: String(item.doctor_id || ''),
      start_at: toDateTimeLocalValue(item.start_at),
      end_at: toDateTimeLocalValue(item.end_at),
      motif: item.motif || '',
      notes: item.notes || '',
      patient_type: item.member_id ? 'SMIE' : 'EXTERNE',
      patient_id: String(item.member_id || item.external_member_id || '')
    });
    setPatientSearch(formatPatientLabel(item));
    setPatientOptions([]);
    setModalError('');
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setShowFormModal(false);
    resetForm();
  };

  const handleSelectPatient = (opt) => {
    setForm((prev) => ({ ...prev, patient_id: String(opt.id) }));
    setPatientSearch(opt.label);
    setPatientOptions([]);
  };

  const submitForm = async (event) => {
    event.preventDefault();
    setSaving(true);
    setModalError('');
    try {
      const payload = {
        type_rendez_vous: 'MEDECIN',
        service: 'CONSULTATION_MEDECIN',
        priority: 'NORMALE',
        doctor_preference: 'SPECIFIQUE',
        doctor_id: Number(form.doctor_id),
        start_at: formatDateTimeForDb(form.start_at),
        end_at: formatDateTimeForDb(form.end_at),
        motif: form.motif.trim(),
        notes: form.notes.trim() || null,
        ...(form.patient_type === 'SMIE'
          ? { member_id: Number(form.patient_id) }
          : { external_member_id: Number(form.patient_id) })
      };
      if (editingId) {
        await rendezVousService.update(editingId, payload);
        toast('success', 'Rendez-vous mis à jour.');
      } else {
        await rendezVousService.create(payload);
        toast('success', 'Rendez-vous créé.');
      }
      setShowFormModal(false);
      resetForm();
      await loadItems();
      await loadCalendar();
      await loadStatusBreakdown();
    } catch (e) {
      setModalError(e.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (item) => {
    setViewingId(item.id);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await rendezVousService.getById(item.id);
      if (res.success) setDetail(res.data || null);
    } catch (e) {
      toast('danger', e.message || 'Détail indisponible');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setViewingId(null);
  };

  const requestStatusAction = (type, item) => {
    closeActionMenu();
    setConfirmAction({ type, item });
    setConfirmReason('');
  };

  const executeStatusAction = async () => {
    if (!confirmAction?.item?.id) return;
    const { type, item } = confirmAction;
    try {
      if (type === 'confirm') await rendezVousService.confirm(item.id);
      if (type === 'finish') await rendezVousService.finish(item.id);
      if (type === 'cancel') await rendezVousService.cancel(item.id, confirmReason.trim());
      if (type === 'absent') await rendezVousService.absent(item.id);
      toast('success', 'Statut mis à jour.');
      setConfirmAction(null);
      setConfirmReason('');
      await loadItems();
      await loadCalendar();
      await loadStatusBreakdown();
      if (detail?.id === item.id) await openDetail(item);
    } catch (e) {
      toast('danger', e.message || 'Transition impossible');
    }
  };

  const detailStatusPill = detail ? getAppointmentStatusPillProps(detail.status, copy) : null;

  return (
    <div className="doctor-dashboard recv-dash-pro--main hd-rdv-page">
      <HeadDoctorPageHeader
        badge={copy.BADGE}
        title={copy.PAGE_TITLE}
        description={copy.PAGE_SUBTITLE}
        actions={
          <button type="button" className="hd-co-create-btn" onClick={openCreate}>
            <Icon name="plus" size={16} aria-hidden />
            {copy.NEW_BUTTON}
          </button>
        }
      />

      <div className="hd-co-shell">
        <section className="hd-co-stats hd-co-stats--4" aria-label="Synthèse des rendez-vous">
          <article className="hd-co-stat-card">
            <span className="hd-co-stat-card__label">{copy.STAT_SHOWN}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.count}</span>
            <span className="hd-co-stat-card__hint">{copy.RESULTS_LABEL}</span>
          </article>
          <article className="hd-co-stat-card hd-co-stat-card--queue">
            <span className="hd-co-stat-card__label">{copy.STAT_PENDING}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.pending}</span>
          </article>
          <article className="hd-co-stat-card hd-co-stat-card--pending">
            <span className="hd-co-stat-card__label">{copy.STAT_CONFIRMED}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.confirmed}</span>
          </article>
          <article className="hd-co-stat-card hd-co-stat-card--success">
            <span className="hd-co-stat-card__label">{copy.STAT_DONE}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.done}</span>
          </article>
        </section>

        <div className="hd-co-toolbar">
          <div className="hd-co-search-wrap">
            <Icon name="search" size={16} className="hd-co-search-icon" aria-hidden />
            <input
              type="search"
              className="hd-co-search-input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_PLACEHOLDER}
            />
          </div>
          <div className="hd-co-toolbar-end">
            <button
              type="button"
              className="hd-co-refresh-btn"
              onClick={() => {
                loadItems();
                loadCalendar();
                loadStatusBreakdown();
              }}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={18} aria-hidden />
            </button>
            <div className="hd-co-toolbar-meta">
              <strong>{loading ? '—' : stats.count}</strong>
              <span>{copy.RESULTS_LABEL}</span>
            </div>
          </div>
        </div>

        <div className="hd-exam-filter-bar hd-rdv-filter-bar">
          <nav className="hd-rdv-status-nav" aria-label={copy.FILTER_STATUS}>
            <span className="hd-rdv-status-nav__label" id="hd-rdv-status-filter-label">
              {copy.FILTER_STATUS}
            </span>
            <div
              className="hd-rdv-status-nav__track"
              role="group"
              aria-labelledby="hd-rdv-status-filter-label"
            >
              {STATUS_FILTERS.map((f) => {
                const filterProps = getAppointmentStatusFilterProps(f.id, copy);
                const isActive = statusFilter === f.id;
                const count = f.id ? statusBreakdown[f.id] : statusBreakdown.all;
                return (
                  <button
                    key={f.id || 'all'}
                    type="button"
                    className={`hd-rdv-status-nav__btn hd-rdv-status-nav__btn--${filterProps.modifier}${
                      isActive ? ' is-active' : ''
                    }`}
                    onClick={() => setStatusFilter(f.id)}
                    aria-pressed={isActive}
                    aria-label={`${filterProps.label}${
                      statusBreakdownLoading ? '' : `, ${count} rendez-vous`
                    }`}
                  >
                    <span className="hd-rdv-status-nav__indicator" aria-hidden />
                    <span className="hd-rdv-status-nav__text">
                      <span className="hd-rdv-status-nav__name">{filterProps.label}</span>
                      <span className="hd-rdv-status-nav__count" aria-hidden>
                        {statusBreakdownLoading ? '—' : count}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>
          <div className="hd-rdv-filter-grid">
            <label className="hd-rdv-filter-field">
              <span className="hd-rdv-filter-field__label">{copy.FILTER_DOCTOR}</span>
              <select
                className="hd-rdv-filter-field__input"
                value={doctorFilter}
                onChange={(e) => setDoctorFilter(e.target.value)}
              >
                <option value="">{copy.FILTER_DOCTOR_ALL}</option>
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {`${d.first_name || ''} ${d.last_name || ''}`.trim()}
                  </option>
                ))}
              </select>
            </label>
            <label className="hd-rdv-filter-field">
              <span className="hd-rdv-filter-field__label">{copy.FILTER_DATE_FROM}</span>
              <input
                type="date"
                className="hd-rdv-filter-field__input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </label>
            <label className="hd-rdv-filter-field">
              <span className="hd-rdv-filter-field__label">{copy.FILTER_DATE_TO}</span>
              <input
                type="date"
                className="hd-rdv-filter-field__input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </label>
          </div>
          <div className="hd-exam-filter-bar__end">
            {hasActiveFilters ? (
              <button type="button" className="hd-co-reset-btn" onClick={resetFilters}>
                {copy.FILTER_RESET}
              </button>
            ) : null}
          </div>
        </div>

        {error ? <Alert variant="danger" className="hd-co-alert mb-0">{error}</Alert> : null}

        {loading ? (
          <div className="hd-co-state" role="status" aria-live="polite">
            <Spinner animation="border" className="hd-co-state__spinner" />
            <p className="hd-co-state__text mb-0">{copy.LOADING}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="hd-co-state hd-co-state--empty">
            <div className="hd-co-state__icon" aria-hidden>
              <Icon name="calendar" size={32} />
            </div>
            <h3 className="hd-co-state__title">{copy.EMPTY_TITLE}</h3>
            <p className="hd-co-state__text">{copy.EMPTY}</p>
            <button type="button" className="hd-co-create-btn" onClick={openCreate}>
              <Icon name="plus" size={16} aria-hidden />
              {copy.NEW_BUTTON}
            </button>
          </div>
        ) : (
          <ul className="hd-rdv-list" aria-label={copy.LIST_ARIA}>
            {items.map((item) => (
              <AppointmentListItem
                key={item.id}
                item={item}
                isViewing={viewingId === item.id}
                copy={copy}
                onOpenMenu={openActionMenu}
                onRowActivate={openDetail}
              />
            ))}
          </ul>
        )}

        <section className="hd-rdv-calendar" aria-label={copy.CALENDAR_TITLE}>
          <div className="hd-rdv-calendar__head">
            <h2 className="hd-rdv-calendar__title">{copy.CALENDAR_TITLE}</h2>
            <p className="hd-rdv-calendar__hint mb-0">{copy.CALENDAR_HINT}</p>
          </div>
          {groupedCalendar.length === 0 ? (
            <p className="hd-rdv-calendar__empty">{copy.CALENDAR_EMPTY}</p>
          ) : (
            <div className="hd-rdv-calendar__days">
              {groupedCalendar.map(([day, list]) => (
                <div key={day} className="hd-rdv-calendar__day">
                  <h3 className="hd-rdv-calendar__day-label">
                    {new Date(`${day}T12:00:00`).toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long'
                    })}
                  </h3>
                  <ul className="hd-rdv-calendar__slots">
                    {list.map((row) => {
                      const pill = getAppointmentStatusPillProps(row.status, copy);
                      return (
                        <li key={row.id} className="hd-rdv-calendar__slot">
                          <button
                            type="button"
                            className="hd-rdv-calendar__slot-btn"
                            onClick={() => openDetail(row)}
                          >
                            <span className="hd-rdv-calendar__slot-time">
                              {formatSlotRange(row)}
                            </span>
                            <span className="hd-rdv-calendar__slot-patient">
                              {formatPatientLabel(row)}
                            </span>
                            <span className={pill.className}>{pill.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {actionMenuItem ? (
        <>
          <button
            type="button"
            className="hd-exam-actions-menu__backdrop"
            aria-label="Fermer le menu actions"
            onClick={closeActionMenu}
          />
          <div
            className={`hd-exam-actions-menu__floating${
              actionMenuPos.placement === 'above' ? ' hd-exam-actions-menu__floating--above' : ''
            }`}
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
            role="menu"
          >
            <button
              type="button"
              className="hd-exam-actions-menu__item"
              role="menuitem"
              onClick={() => openDetail(actionMenuItem)}
            >
              <Icon name="eye" size={15} />
              <span>{copy.ACTION_VIEW}</span>
            </button>
            {(actionMenuItem.status === 'EN_ATTENTE' || actionMenuItem.status === 'CONFIRME') && (
              <button
                type="button"
                className="hd-exam-actions-menu__item"
                role="menuitem"
                onClick={() => openEdit(actionMenuItem)}
              >
                <Icon name="edit" size={15} />
                <span>{copy.ACTION_EDIT}</span>
              </button>
            )}
            {actionMenuItem.status === 'EN_ATTENTE' && (
              <button
                type="button"
                className="hd-exam-actions-menu__item"
                role="menuitem"
                onClick={() => requestStatusAction('confirm', actionMenuItem)}
              >
                <Icon name="check" size={15} />
                <span>{copy.ACTION_CONFIRM}</span>
              </button>
            )}
            {actionMenuItem.status === 'CONFIRME' && (
              <button
                type="button"
                className="hd-exam-actions-menu__item"
                role="menuitem"
                onClick={() => requestStatusAction('finish', actionMenuItem)}
              >
                <Icon name="check" size={15} />
                <span>{copy.ACTION_FINISH}</span>
              </button>
            )}
            {(actionMenuItem.status === 'EN_ATTENTE' || actionMenuItem.status === 'CONFIRME') && (
              <>
                <div className="hd-exam-actions-menu__separator" />
                <button
                  type="button"
                  className="hd-exam-actions-menu__item hd-exam-actions-menu__danger"
                  role="menuitem"
                  onClick={() => requestStatusAction('cancel', actionMenuItem)}
                >
                  <Icon name="times" size={15} />
                  <span>{copy.ACTION_CANCEL}</span>
                </button>
                <button
                  type="button"
                  className="hd-exam-actions-menu__item"
                  role="menuitem"
                  onClick={() => requestStatusAction('absent', actionMenuItem)}
                >
                  <Icon name="user" size={15} />
                  <span>{copy.ACTION_ABSENT}</span>
                </button>
              </>
            )}
          </div>
        </>
      ) : null}

      <AppointmentFormModal
        show={showFormModal}
        copy={copy}
        form={form}
        setForm={setForm}
        doctors={doctors}
        editingId={editingId}
        saving={saving}
        modalError={modalError}
        patientSearch={patientSearch}
        setPatientSearch={setPatientSearch}
        patientOptions={patientOptions}
        patientSearchLoading={patientSearchLoading}
        onSelectPatient={handleSelectPatient}
        availability={availability}
        onClose={closeFormModal}
        onSubmit={submitForm}
      />

      <Modal
        show={!!detail || detailLoading}
        onHide={closeDetail}
        centered
        size="lg"
        className="hd-dis-modal-root hd-rdv-detail-modal-root"
        backdropClassName="hd-dis-modal-backdrop"
        dialogClassName="hd-dis-modal-dialog"
        contentClassName="hd-dis-modal__content"
      >
        <div className="hd-dis-modal__header">
          <div className="hd-dis-modal__header-main">
            <span className="hd-dis-modal__icon" aria-hidden>
              <Icon name="calendar" size={22} />
            </span>
            <div className="hd-dis-modal__titles">
              <span className="hd-dis-modal__mode hd-dis-modal__mode--edit">{copy.DETAIL_BADGE}</span>
              <h2 className="hd-dis-modal__title">{copy.DETAIL_TITLE}</h2>
              {detail?.reference ? (
                <p className="hd-dis-modal__subtitle mb-0">{detail.reference}</p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="hd-dis-modal__close"
            onClick={closeDetail}
            aria-label={copy.MODAL_CLOSE_ARIA}
          >
            <Icon name="times" size={18} aria-hidden />
          </button>
        </div>
        <div className="hd-dis-modal__body">
          {detailLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          ) : !detail ? (
            <Alert variant="warning" className="mb-0">
              {copy.DETAIL_UNAVAILABLE}
            </Alert>
          ) : (
            <div className="hd-rdv-detail">
              <div className="hd-rdv-detail__row">
                <span className="hd-rdv-detail__label">{copy.DETAIL_PATIENT}</span>
                <span className="hd-rdv-detail__value">{formatPatientLabel(detail)}</span>
              </div>
              <div className="hd-rdv-detail__row">
                <span className="hd-rdv-detail__label">{copy.DETAIL_DOCTOR}</span>
                <span className="hd-rdv-detail__value">{formatDoctorName(detail)}</span>
              </div>
              <div className="hd-rdv-detail__row">
                <span className="hd-rdv-detail__label">{copy.DETAIL_SLOT}</span>
                <span className="hd-rdv-detail__value">{formatSlotRange(detail)}</span>
              </div>
              <div className="hd-rdv-detail__row">
                <span className="hd-rdv-detail__label">{copy.DETAIL_MOTIF}</span>
                <span className="hd-rdv-detail__value">{detail.motif || '—'}</span>
              </div>
              {detail.notes ? (
                <div className="hd-rdv-detail__row hd-rdv-detail__row--block">
                  <span className="hd-rdv-detail__label">{copy.DETAIL_NOTES}</span>
                  <p className="hd-rdv-detail__notes mb-0">{detail.notes}</p>
                </div>
              ) : null}
              <div className="hd-rdv-detail__row">
                <span className="hd-rdv-detail__label">{copy.DETAIL_STATUS}</span>
                {detailStatusPill ? (
                  <span className={detailStatusPill.className}>{detailStatusPill.label}</span>
                ) : null}
              </div>
              <div className="hd-rdv-detail__history">
                <h3 className="hd-rdv-detail__history-title">{copy.DETAIL_HISTORY}</h3>
                {Array.isArray(detail.history) && detail.history.length > 0 ? (
                  <ul className="hd-rdv-detail__history-list">
                    {detail.history.map((h) => (
                      <li key={h.id}>
                        <span className="hd-rdv-detail__history-date">
                          {new Date(h.created_at).toLocaleString('fr-FR')}
                        </span>
                        <span className="hd-rdv-detail__history-action">{h.action}</span>
                        {h.old_status || h.new_status ? (
                          <span className="hd-rdv-detail__history-transition">
                            {h.old_status || '—'} → {h.new_status || '—'}
                          </span>
                        ) : null}
                        {h.note ? <span className="hd-rdv-detail__history-note">{h.note}</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="hd-rdv-detail__history-empty mb-0">{copy.DETAIL_HISTORY_EMPTY}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        show={!!confirmAction}
        onHide={() => setConfirmAction(null)}
        centered
        className="hd-dis-modal-root hd-rdv-confirm-modal-root"
        backdropClassName="hd-dis-modal-backdrop"
        dialogClassName="hd-dis-modal-dialog hd-dis-modal-dialog--sm"
        contentClassName="hd-dis-modal__content"
      >
        <div className="hd-dis-modal__header">
          <div className="hd-dis-modal__header-main">
            <span className="hd-dis-modal__icon" aria-hidden>
              <Icon name="alertCircle" size={22} />
            </span>
            <div className="hd-dis-modal__titles">
              <h2 className="hd-dis-modal__title">{copy.CONFIRM_TITLE}</h2>
              {confirmAction ? (
                <p className="hd-dis-modal__subtitle mb-0">
                  {CONFIRM_LABELS[confirmAction.type]} — {confirmAction.item.reference}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="hd-dis-modal__close"
            onClick={() => setConfirmAction(null)}
            aria-label={copy.MODAL_CLOSE_ARIA}
          >
            <Icon name="times" size={18} aria-hidden />
          </button>
        </div>
        <div className="hd-dis-modal__body">
          {confirmAction?.type === 'cancel' ? (
            <Form.Group>
              <Form.Label className="hd-dis-modal__label">{copy.CONFIRM_CANCEL_REASON}</Form.Label>
              <Form.Control
                className="hd-dis-modal__input"
                value={confirmReason}
                onChange={(e) => setConfirmReason(e.target.value)}
                required
              />
            </Form.Group>
          ) : (
            <p className="mb-0 small text-muted">{copy.CONFIRM_HINT}</p>
          )}
        </div>
        <div className="hd-dis-modal__footer">
          <button
            type="button"
            className="hd-dis-modal__btn-cancel"
            onClick={() => setConfirmAction(null)}
          >
            {copy.BTN_CANCEL}
          </button>
          <button
            type="button"
            className="hd-co-create-btn"
            onClick={executeStatusAction}
            disabled={confirmAction?.type === 'cancel' && !confirmReason.trim()}
          >
            {copy.BTN_CONFIRM}
          </button>
        </div>
      </Modal>
    </div>
  );
}
